/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Avatar,
  InputAdornment,
  InputLabel,
  InputLabelProps,
  MenuItem,
  menuItemClasses,
  OutlinedInput,
  SelectProps,
  Stack,
} from '@mui/material';
import {
  ForwardedRef,
  forwardRef,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { HBIcon } from '../HBIcon/HBIcon';
import {
  HBFormControlRootStyle,
  HBHelperTextStyle,
  HBHelperTextWrapperStyle,
  HBSelectRootStyle,
} from './HBSelect.styles';
import { HBIconButton } from '../HBIconButton/HBIconButton';

export interface HBSelectProps extends Omit<SelectProps, 'ref'> {
  label: string;
  menuItem: Array<{
    title: ReactNode;
    value: any;
    iconPath?: string | ReactNode;
    color?: string;
    'data-test-id'?: string;
  }>;
  noneOption?: string;
  inputLabelProps?: InputLabelProps;
  helperText?: string;
  showErrorMessage?: boolean;
  clearable?: boolean;
  onClear?: () => void;
}

export const HBSelect = forwardRef(
  <T extends HTMLInputElement>(
    {
      noneOption,
      menuItem,
      inputLabelProps,
      helperText,
      showErrorMessage,
      ...props
    }: HBSelectProps,
    ref: ForwardedRef<T | HTMLInputElement>,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
      const handler = () => {
        setIsOpen(false);
      };
      window.addEventListener('scroll', handler);
      return () => {
        window.removeEventListener('scroll', handler);
      };
    }, []);

    return (
      <HBFormControlRootStyle
        style={props.style}
        sx={props.sx}
        size={props.size}
        fullWidth={props.fullWidth}
        required={props.required}
        disabled={props.disabled}
      >
        <InputLabel {...inputLabelProps}>{props.label}</InputLabel>
        <HBSelectRootStyle
          open={props.disabled ? false : isOpen}
          onOpen={() => {
            if (!props.disabled) {
              setIsOpen(true);
            }
          }}
          onClose={() => {
            setIsOpen(false);
          }}
          ref={(el: T | null) => {
            selectRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) (ref as MutableRefObject<unknown>).current = el;
          }}
          input={
            <OutlinedInput
              label={props.label}
              endAdornment={
                props.clearable ? (
                  <InputAdornment position="end">
                    <HBIconButton
                      variant="text"
                      sx={{ mr: 3 }}
                      iconSize="small"
                      icon="timesCircle"
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onClear?.();
                      }}
                    />
                  </InputAdornment>
                ) : undefined
              }
            />
          }
          {...props}
        >
          {noneOption && (
            <MenuItem value="">
              <em>{noneOption}</em>
            </MenuItem>
          )}

          {menuItem?.map((item) => (
            <MenuItem
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                [`&.${menuItemClasses.root}`]: {
                  maxWidth: selectRef?.current?.offsetWidth || 'auto',
                },
              }}
              key={String(item.value)}
              value={item.value}
              data-test-id={item['data-test-id']}
            >
              {item?.iconPath && typeof item?.iconPath === 'string' && (
                <Avatar
                  sx={{
                    mr: 2,
                    height: 24,
                    width: 24,
                  }}
                  sizes="small"
                  variant="circular"
                  src={`${process.env['NEXT_PUBLIC_CDN']}/${item.iconPath}`}
                  alt={String(item?.title || '')}
                />
              )}
              {item?.color && (
                <Stack
                  sx={{
                    mr: 2,
                    height: 24,
                    width: 24,
                    backgroundColor: item.color,
                    borderRadius: '50%',
                  }}
                />
              )}
              {item?.iconPath && typeof item?.iconPath !== 'string' && item.iconPath}
              {item.title}
            </MenuItem>
          ))}
        </HBSelectRootStyle>
        {helperText && showErrorMessage && (
          <HBHelperTextWrapperStyle sx={{ color: props.error ? 'error.main' : 'grey.900' }}>
            <HBIcon size="small" type="infoCircle" />
            <HBHelperTextStyle>{helperText}</HBHelperTextStyle>
          </HBHelperTextWrapperStyle>
        )}
      </HBFormControlRootStyle>
    );
  },
);

HBSelect.displayName = 'HBSelect';
